import type { ChessPiece } from "../models/base-model";
import type { CaptureLog, CaptureSummary, Move } from "../models/move-model";
import type { ChessPosition } from "../models/position-model";
import { Chess } from "chess.js";

type RelativeCaptureSummary = {
    user: CaptureLog;
    opponent: CaptureLog;
};

type GetRelativeCaptureLogProps = {
    position?: ChessPosition;
    playerIsWhite: boolean;
};

const getRelativeCaptureLog = ({
    position,
    playerIsWhite,
}: GetRelativeCaptureLogProps): RelativeCaptureSummary => {
    if (!position) {
        return {
            user: { pieces: [], score: 0 },
            opponent: { pieces: [], score: 0 },
        };
    }

    const user = playerIsWhite
        ? position.captureSummary.w
        : position.captureSummary.b;
    const opponent = playerIsWhite
        ? position.captureSummary.b
        : position.captureSummary.w;

    return {
        user,
        opponent,
    };
};

const ChessPieceScore: Record<ChessPiece, number> = {
    p: 1,
    n: 3,
    b: 3,
    r: 5,
    q: 9,
    k: 0, // king is invaluable
};

const calculateSingleScore = (pieces: ChessPiece[]) =>
    pieces.reduce((acc, piece) => acc + ChessPieceScore[piece], 0);

const getScore = (summary: CaptureSummary) => {
    const whiteTotal = calculateSingleScore(summary.w.pieces);
    const blackTotal = calculateSingleScore(summary.b.pieces);

    let whiteScore = 0;
    let blackScore = 0;

    if (whiteTotal > blackTotal) {
        whiteScore = whiteTotal - blackTotal;
    } else if (blackTotal > whiteTotal) {
        blackScore = blackTotal - whiteTotal;
    }

    return {
        whiteTotal,
        blackTotal,
        whiteScore,
        blackScore,
    };
};

const getFenContext = (position: ChessPosition) => {
    const currentFen = position.fen;
    const previousFen = position.previousFen;

    const helperEngine = new Chess();
    helperEngine.load(currentFen);

    const wasWhiteMove = helperEngine.turn() !== "w";
    const isWhiteToMove = helperEngine.turn() === "w";

    return {
        currentFen,
        previousFen,
        helperEngine,
        wasWhiteMove,
        isWhiteToMove,
    };
};

const getMoveForPosition = (
    position: ChessPosition,
    moveMap: Record<string, Move>
): Move | undefined => {
    const previousMove = position.previousMove;
    if (!previousMove) {
        return undefined;
    }
    return moveMap[previousMove];
};

export const PositionUtil = {
    getRelativeCaptureLog,
    getScore,
    getFenContext,
    getMoveForPosition,
};
